// Basic
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
// Store & actions
import useSimsStore from '@/app/store/modules/sims';
import useSessionStore from '@/app/store/session';
// Components
import Page from '@/common-utils/components/structure/page/Page';
import SimCardsTable from '@/app/modules/sims/components/tables/SimCardsTable';
import SimGroupsTable from '@/app/modules/sims/components/tables/SimGroupsTable';
import Entity from '@/common-utils/components/renders/entity/Entity';
import ModalMySimsMultiActivation from '@/app/modules/sims/components/modals/multipleActivation/ModalMySimsMultiActivation';
import ModalClientSimsMultiActivation from '@/app/modules/sims/components/modals/multipleActivation/ModalClientSimsMultiActivation';
import ModalAddSimCardToOrganization from '@/app/modules/sims/components/modals/addSimCard/ModalAddSimCardToOrganization';
import ModalAddSimCardToClient from '@/app/modules/sims/components/modals/addSimCard/ModalAddSimCardToClient';
import ModalOrderSimCard from '@/app/modules/sims/components/modals/orderSimCard/ModalOrderSimCard';
import PageActions from '@/common-utils/components/renders/pageActions/PageActions';
import TooltipHelper from '@/common-utils/components/renders/tooltip/TooltipHelper';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/common-utils/components/ui/tabs';
// Common
import RouteUrls from '../enums/RouteUrls';
import ICONS from '@/common-utils/enums/icons';
import Permissions from '@/app/permissions/Permissions';
import useTabManager from '../hooks/useTabManager';
import hasRequiredPermissions from '@/common-utils/functions/hasRequiredPermissions';
import SIM_CARDS_TABS from '../enums/tabs/SimCardsTabs';
import { extractQueriesFromUrl } from '@/common-utils/functions/restUtils';
import setFiltersEventName from '@/common-utils/components/renders/dataTable/utils/setFiltersEventName';
import Icon from '@/common-utils/components/renders/icon/Icon';
// Lang
import { nsSimCard } from '@/app/i18n/namespaces';
/**
 * @returns {JSX} SimCards
 */
function SimCards() {

    // URL Params
    const navigate = useNavigate();
    const location = useLocation();
    const { view } = extractQueriesFromUrl(location.search);

    // Store
    const session = useSessionStore(s => s.session);
    const permissions = session.permissions;
    const storeRefetchSimCards = useSimsStore(s => s.storeRefetchSimCards);

    // State
    const [activeTab, setActiveTab] = useState(Object.values(SIM_CARDS_TABS).find(tab => tab.name === view)?.name ?? SIM_CARDS_TABS.DEFAULT.name);
    const [showModalOrderSim, setShowModalOrderSim] = useState(false);
    const [showMySimsMultiActivation, setShowMySimsMultiActivation] = useState(false);
    const [showClientSimsMultiActivation, setShowClientSimsMultiActivation] = useState(false);
    const [showModalAddSimCardToOrganization, setShowModalAddSimCardToOrganization] = useState(false);
    const [showModalAddSimCardToClient, setShowModalAddSimCardToClient] = useState(false);

    // Hooks
    // Tab Manager
    useTabManager({
        pathname: location.pathname,
        view: view,
        activeTab: activeTab,
        setActiveTab: setActiveTab,
        tabs: SIM_CARDS_TABS,
        navigate: navigate
    });

    return (
        <Page>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <Page.Header
                    route={RouteUrls.SIM_CARDS}
                    routeBase={RouteUrls.SIM_CARDS}
                >
                    <TabsList>
                        <TabsTrigger tab={SIM_CARDS_TABS.SIM_CARDS} activeTab={activeTab} permissions={permissions} >
                            <Entity entity='page.sim_cards.tabs.sims' />
                        </TabsTrigger>
                        <TooltipHelper
                            asChild
                            contentClass='max-w-sm'
                            trigger={
                                <TabsTrigger tab={SIM_CARDS_TABS.SIM_GROUPS} activeTab={activeTab} permissions={permissions} >
                                    <Entity entity='page.sim_cards.tabs.simPools' />
                                    <Icon icon={ICONS.HELP} className='ml-1' />
                                </TabsTrigger>
                            }
                            content={<Entity entity='sim_pools.tooltip.sim_groups_tab' namespace={nsSimCard} />}
                        />
                    </TabsList>
                </Page.Header>
                <Page.Body dataCy='page-sim-cards'>
                    <TabsContent tab={SIM_CARDS_TABS.SIM_CARDS} permissions={permissions}>
                        <SimCardsTable
                            filtersEventName={setFiltersEventName('sim_cards')}
                            pageActions={
                                <PageActions actions={[
                                    {
                                        icon: ICONS.SHOPPING_CART,
                                        entity: 'actions.row.order_sim',
                                        namespace: nsSimCard,
                                        onClick: () => setShowModalOrderSim(true),
                                        visible: true,
                                        disabled: !hasRequiredPermissions({
                                            permissions: permissions,
                                            requiredPermissions: [
                                                Permissions.SIM_PASS_ORDER.name
                                            ]
                                        }),
                                        tooltipForbidden: 'title.permissions.access_denied'
                                    },
                                    {
                                        isDropdown: true,
                                        visible: true,
                                        trigger: {
                                            icon: ICONS.SUBSCRIPTION_ACTIVATE,
                                            entity: 'actions.row.activate_sim_cards.title',
                                            namespace: nsSimCard,
                                            disabled: !hasRequiredPermissions({
                                                permissions: permissions,
                                                requiredPermissions: [
                                                    Permissions.SIM_ACTIVATE.name,
                                                    Permissions.GET_OFFERS_FOR_SIM.name
                                                ]
                                            }),
                                            tooltipForbidden: 'title.permissions.access_denied'
                                        },
                                        subActions: [
                                            {
                                                entity: 'actions.row.activate_sim_cards.organization',
                                                namespace: nsSimCard,
                                                onClick: () => setShowMySimsMultiActivation(true),
                                                visible: true,
                                                disabled: !hasRequiredPermissions({
                                                    permissions: permissions,
                                                    requiredPermissions: [
                                                        Permissions.SIM_ACTIVATE.name,
                                                        Permissions.GET_OFFERS_FOR_SIM.name
                                                    ]
                                                }),
                                                tooltipForbidden: 'title.permissions.access_denied'
                                            },
                                            {
                                                entity: 'actions.row.activate_sim_cards.client',
                                                namespace: nsSimCard,
                                                onClick: () => setShowClientSimsMultiActivation(true),
                                                visible: true,
                                                disabled: !hasRequiredPermissions({
                                                    permissions: permissions,
                                                    requiredPermissions: [
                                                        Permissions.SIM_ACTIVATE.name,
                                                        Permissions.GET_OFFERS_FOR_SIM.name
                                                    ]
                                                }),
                                                tooltipForbidden: 'title.permissions.access_denied'
                                            }
                                        ]
                                    },
                                    {
                                        isDropdown: true,
                                        trigger: {
                                            icon: ICONS.ADD,
                                            entity: 'actions.row.add_to.title',
                                            namespace: nsSimCard,
                                            disabled: !hasRequiredPermissions({
                                                permissions: permissions,
                                                requiredPermissions: [
                                                    Permissions.SIM_CREATE_SUBSCRIPTION.name
                                                ]
                                            }),
                                            tooltipForbidden: 'title.permissions.access_denied'
                                        },
                                        visible: session.organization === '7854085d-a318-49f0-965b-47d693ec1cd8',
                                        subActions: [
                                            {
                                                entity: 'actions.row.add_to.organization',
                                                namespace: nsSimCard,
                                                onClick: () => setShowModalAddSimCardToOrganization(true),
                                                visible: true,
                                                disabled: !hasRequiredPermissions({
                                                    permissions: permissions,
                                                    requiredPermissions: [
                                                        Permissions.SIM_CREATE_SUBSCRIPTION.name
                                                    ]
                                                }),
                                                tooltipForbidden: 'title.permissions.access_denied'
                                            },
                                            {
                                                entity: 'actions.row.add_to.client',
                                                namespace: nsSimCard,
                                                onClick: () => setShowModalAddSimCardToClient(true),
                                                visible: true,
                                                disabled: !hasRequiredPermissions({
                                                    permissions: permissions,
                                                    requiredPermissions: [
                                                        Permissions.SIM_CREATE_SUBSCRIPTION.name
                                                    ]
                                                }),
                                                tooltipForbidden: 'title.permissions.access_denied'
                                            }
                                        ]
                                    }
                                ]}
                                    retractable
                                />
                            }
                        />
                    </TabsContent>
                    <TabsContent tab={SIM_CARDS_TABS.SIM_GROUPS} permissions={permissions}>
                        <SimGroupsTable filtersEventName={setFiltersEventName('sim_groups')} />
                    </TabsContent>
                </Page.Body>
            </Tabs>
            {
                showMySimsMultiActivation
                && <ModalMySimsMultiActivation
                    isOpen={showMySimsMultiActivation}
                    setIsOpen={setShowMySimsMultiActivation}
                />
            }
            {
                showClientSimsMultiActivation
                && <ModalClientSimsMultiActivation
                    isOpen={showClientSimsMultiActivation}
                    setIsOpen={setShowClientSimsMultiActivation}
                />
            }
            {
                showModalOrderSim &&
                <ModalOrderSimCard
                    isOpen={showModalOrderSim}
                    setIsOpen={setShowModalOrderSim}
                />
            }
            {
                showModalAddSimCardToOrganization &&
                <ModalAddSimCardToOrganization
                    isOpen={showModalAddSimCardToOrganization}
                    setIsOpen={setShowModalAddSimCardToOrganization}
                    setLaunchRefetch={storeRefetchSimCards}
                />
            }
            {
                showModalAddSimCardToClient &&
                <ModalAddSimCardToClient
                    isOpen={showModalAddSimCardToClient}
                    setIsOpen={setShowModalAddSimCardToClient}
                    setLaunchRefetch={storeRefetchSimCards}
                />
            }
        </Page >
    );
}

export default SimCards;